// Currency parsing + formatting helpers.
//
// Timestop stores amounts in two ways:
//   - In the DB and on the wire: minor units (integer cents/grosze).
//   - For display: divided by `div` (default 100) and formatted with `fs`
//     (a printf-like template) plus the ISO code.
//
// splitCurrency() handles user-pasted strings ("1,234.56 PLN", "1.234,56 €")
// and figures out which separator is the decimal one based on local
// heuristics — it used to live inside the modal for inline editing.

import type { TimestopState } from '../state';
import type { CurrencyConfig, Terminal } from '../types';

// Parse a number string that may use either US (1,234.56) or European
// (1.234,56) formatting. Returns NaN if it can't figure it out.
function parseNumberString(numStr: string): number {
  numStr = numStr.replace(/\s/g, '');
  const dots = (numStr.match(/\./g) || []).length;
  const commas = (numStr.match(/,/g) || []).length;

  if (dots > 0 && commas > 0) {
    const lastDot = numStr.lastIndexOf('.');
    const lastComma = numStr.lastIndexOf(',');
    if (lastComma > lastDot) {
      // European: 1.234,56 or 1.234.567,89
      return parseFloat(numStr.replace(/\./g, '').replace(',', '.'));
    }
    // US: 1,234.56
    return parseFloat(numStr.replace(/,/g, ''));
  } else if (commas > 0) {
    if (/,\d{1,2}$/.test(numStr)) {
      return parseFloat(numStr.replace(',', '.'));
    }
    return parseFloat(numStr.replace(/,/g, ''));
  } else if (dots > 0) {
    if (/\.\d{1,2}$/.test(numStr)) {
      return parseFloat(numStr);
    }
    return parseFloat(numStr.replace(/\./g, ''));
  }
  return parseFloat(numStr);
}

export interface SplitResult {
  value: number | null;
  symbol: string | null;
  pre: boolean | null;
}

export function splitCurrency(currencyStr: unknown): SplitResult {
  if (typeof currencyStr !== 'string') {
    return { value: null, symbol: null, pre: null };
  }

  const str = currencyStr.trim();
  const numMatch = str.match(/([-+]\s*)?([\d.,]+\d)/);
  if (!numMatch) {
    return { value: null, symbol: null, pre: null };
  }

  const sign = numMatch[1] || '';
  const numPart = numMatch[2];
  const fullNumber = sign + numPart;

  const value = parseNumberString(fullNumber);

  // Extract symbol — anything that isn't the number itself.
  const symbolMatch = str.match(/[^\d.,\s-+]+/);
  const symbol = symbolMatch ? symbolMatch[0] : null;
  // pre=true when the symbol is a leading currency glyph (€, $, £, ¥, ₽).
  // In the original code this was the same boolean; callers don't use it
  // yet, so we expose it as boolean|null to match reality.
  const pre: boolean | null = symbol ? /^[€$£¥₽]/.test(symbol) : null;

  return {
    value: isNaN(value) ? null : value,
    symbol,
    pre,
  };
}

// Globally defined fallback CurrencyConfig.
//
// Whenever a renderer asks for an ISO that the terminal didn't declare in
// `cur_recv` (missing `set_recv.cur_recv`, terminal booted without
// currencies configured, an ISO shows up in `acc_long.in` that wasn't
// pre-declared, etc.) we still need to format the number — the call sites
// historically fell back to `value.toFixed(2) + ' ' + iso` ad-hoc, three
// times in accounting.ts alone and once in accounting-group.ts. That was
// fragile and easy to drift.
//
// The contract: a fallback config is a real CurrencyConfig with
//   - div: 100 (minor units → major units, the default the project ships with)
//   - fs:  '%.2f <ISO>' (two-decimal display with ISO appended as a suffix,
//                         matching what the hand-rolled fallback produced)
// so `formatCurrency(v, fallback)` and `formatDividedCurrency(v, fallback)`
// produce the exact same string the old ternary did, with no special-case
// branch in the caller.
export const FALLBACK_CURRENCY_DIV = 100;
export function buildFallbackCurrency(iso: string): CurrencyConfig {
  return { ISO: iso, div: FALLBACK_CURRENCY_DIV, fs: '%.2f ' + iso };
}

// Build a quick ISO→config lookup for a terminal's cur_recv map.
// The result is cached onto the terminal as `currencyLookup` so we don't
// rebuild it on every render call.
export function buildCurrencyLookup(
  curRecv: Terminal['cur_recv'] | undefined | null,
): Record<string, CurrencyConfig> {
  const lookup: Record<string, CurrencyConfig> = {};
  if (!curRecv) return lookup;
  for (const cfg of Object.values(curRecv)) {
    if (cfg && cfg.ISO) {
      lookup[String(cfg.ISO).toUpperCase()] = cfg;
    }
  }
  return lookup;
}

// Look up the currency config for an ISO code on a terminal. Caches the
// result onto the terminal so subsequent calls are O(1).
//
// Returns a real `CurrencyConfig` for any ISO — the lookup order is:
//
//   1. The terminal's own `cur_recv` (per-deployment, authoritative
//      for that terminal).
//   2. The BO-wide default from `state.currencyDefaults` (hydrated
//      from /api/currencies after login; per-ISO div/fs the admin
//      configures once and ships to every terminal that hasn't
//      declared its own override). This is the
//      "globally defined fallback structure" the project carries
//      server-side, sourced from the DB.
//   3. The hard-coded JS fallback `buildFallbackCurrency(iso)`
//      ({div:100, fs:'%.2f '+iso}). Last-resort for hosts whose
//      /api/currencies returns an empty map (table doesn't exist
//      yet, fresh deploy before the migration has run, etc.).
//
// Callers can therefore feed the result straight into `formatCurrency` /
// `formatDividedCurrency` without a null check.
//
// `state` is OPTIONAL. Callers that don't have state in scope (e.g.
// pure helper contexts, unit tests) can pass undefined; the lookup
// then degrades to terminal-only + hard-coded fallback.
export function getCurrencyByISO(
  terminal: Terminal,
  iso: string,
  state?: TimestopState | null,
): CurrencyConfig {
  const upper = iso.toUpperCase();
  if (!terminal.currencyLookup) {
    terminal.currencyLookup = buildCurrencyLookup(terminal.cur_recv);
  }
  const fromTerminal = terminal.currencyLookup[upper];
  if (fromTerminal) return fromTerminal;
  const fromDefaults = state?.currencyDefaults?.[upper];
  if (fromDefaults) return fromDefaults;
  return buildFallbackCurrency(upper);
}

// Format a value (already in minor units) as a human-readable string.
// If `currencyConfig` is missing, falls back to `.toFixed(2)`.
export function formatCurrency(value: number | null | undefined, currencyConfig: CurrencyConfig | null | undefined): string {
  if (value == null || isNaN(value as number)) return '—';
  if (!currencyConfig) {
    return (value as number).toFixed(2);
  }

  const fs = currencyConfig.fs || '%.2f';
  const div = currencyConfig.div || 100;
  const amount = (value as number) / div;

  if (fs.includes('%')) {
    const decimals = (fs.match(/\.(\d+)f/) || ['', '2'])[1];
    return fs.replace(/%[\d.]*f/, amount.toFixed(parseInt(decimals, 10)));
  }

  return `${amount.toFixed(2)} ${currencyConfig.ISO || ''}`;
}

// Format a value (already in minor units) as a human-readable string.
// If `currencyConfig` is missing, falls back to `.toFixed(2)`.
// not dividing what is arleady divided
export function formatDividedCurrency(value: number | null | undefined, currencyConfig: CurrencyConfig | null | undefined): string {
  if (value == null || isNaN(value as number)) return '—';
  if (!currencyConfig) {
    return (value as number).toFixed(2);
  }

  const fs = currencyConfig.fs || '%.2f';
  const amount = (value as number) ;

  if (fs.includes('%')) {
    const decimals = (fs.match(/\.(\d+)f/) || ['', '2'])[1];
    return fs.replace(/%[\d.]*f/, amount.toFixed(parseInt(decimals, 10)));
  }

  return `${amount.toFixed(2)} ${currencyConfig.ISO || ''}`;
}

// Convenience wrapper for `formatCurrency` that resolves the ISO via the
// terminal's lookup table. Optional `state` is forwarded to
// `getCurrencyByISO` so DB-sourced defaults are honoured.
export function formatCurrencyByISO(
  terminal: Terminal,
  value: number | null | undefined,
  iso: string,
  state?: TimestopState | null,
): string {
  const config = getCurrencyByISO(terminal, iso, state);
  return formatCurrency(value, config);
}
// Convenience wrapper for `formatCurrency` that resolves the ISO via the
// terminal's lookup table.
// not dividing what is arleady divided
export function formatDividedCurrencyByISO(
  terminal: Terminal,
  value: number | null | undefined,
  iso: string,
  state?: TimestopState | null,
): string {
  const config = getCurrencyByISO(terminal, iso, state);
  return formatDividedCurrency(value, config);
}

// Sum totals across one or both accounting periods, grouped by ISO code.
// `period` is optional — when null, both `acc_long` and `acc_shor` are summed.
export function getTotalByCurrency(terminal: Terminal | null | undefined, period: string | null = null): Record<string, number> {
  const tab: Record<string, number> = {};
  const periods = period ? [period] : ['acc_shor', 'acc_long'];

  for (const p of periods) {
    const acc = terminal?.[p];
    if (!acc || typeof acc !== 'object') continue;
    const inSection = (acc as { in?: Record<string, Record<string, number> | null | undefined> }).in;
    if (!inSection) continue;

    for (const struct of Object.values(inSection)) {
      if (!struct) continue;
      for (const [currency, value] of Object.entries(struct)) {
        tab[currency] = (tab[currency] || 0) + (value || 0);
      }
    }
  }

  return tab;
}